import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  CreditCard, 
  Briefcase, 
  Users, 
  User, 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2, 
  Home,
  Building2,
  Wallet,
  ShoppingCart,
  Car,
  GraduationCap,
  Phone,
  X
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Debt Level", icon: CreditCard },
  { id: 2, title: "Employment", icon: Briefcase },
  { id: 3, title: "Creditors", icon: Users },
  { id: 4, title: "Contact", icon: User },
];

const EMPLOYMENT_OPTIONS = [
  { value: "employed_full", label: "Employed Full-Time", icon: Briefcase },
  { value: "employed_part", label: "Employed Part-Time", icon: Briefcase },
  { value: "self_employed", label: "Self-Employed", icon: Building2 },
  { value: "unemployed", label: "Unemployed", icon: User },
  { value: "retired", label: "Retired", icon: Home },
  { value: "benefits", label: "On Benefits", icon: Wallet },
];

const RESIDENTIAL_OPTIONS = [
  { value: "homeowner", label: "Homeowner", icon: Home },
  { value: "tenant", label: "Renting", icon: Building2 },
  { value: "living_family", label: "Living with Family", icon: Users },
];

const DEBT_TYPES = [
  { value: "credit_cards", label: "Credit Cards", icon: CreditCard },
  { value: "personal_loans", label: "Personal Loans", icon: Wallet },
  { value: "overdrafts", label: "Overdrafts", icon: Building2 },
  { value: "store_cards", label: "Store Cards", icon: ShoppingCart },
  { value: "car_finance", label: "Car Finance", icon: Car },
  { value: "student_loans", label: "Student Loans", icon: GraduationCap },
  { value: "payday_loans", label: "Payday Loans", icon: Phone },
  { value: "catalogues", label: "Catalogues", icon: ShoppingCart },
];

const MultiStepForm = ({ initialDebt = 15000, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  
  const [formData, setFormData] = useState({
    debt_amount: initialDebt,
    employment_status: "",
    residential_status: "",
    monthly_income: 2000,
    number_of_creditors: 3,
    debt_types: [],
    full_name: "",
    email: "",
    phone: "",
    consent_marketing: false,
    consent_terms: false,
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.debt_amount >= 5000;
      case 2:
        if (!formData.employment_status) {
          toast.error("Please select your employment status");
          return false;
        }
        if (!formData.residential_status) {
          toast.error("Please select your residential status");
          return false;
        }
        return true;
      case 3:
        if (formData.number_of_creditors < 1) {
          toast.error("Please enter number of creditors");
          return false;
        }
        return true;
      case 4:
        if (!formData.full_name || formData.full_name.length < 2) {
          toast.error("Please enter your full name");
          return false;
        }
        if (!formData.email || !formData.email.includes("@")) {
          toast.error("Please enter a valid email address");
          return false;
        }
        if (!formData.phone || formData.phone.length < 10) {
          toast.error("Please enter a valid phone number");
          return false;
        }
        if (!formData.consent_terms) {
          toast.error("Please accept the terms and conditions");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/leads`, formData);
      setSubmitResult(response.data);
      setIsComplete(true);
      toast.success("Your application has been submitted successfully!");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("There was an error submitting your application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDebtType = (value) => {
    setFormData(prev => ({
      ...prev,
      debt_types: prev.debt_types.includes(value)
        ? prev.debt_types.filter(t => t !== value)
        : [...prev.debt_types, value]
    }));
  };

  const progressPercent = (currentStep / 4) * 100;

  // Completed state
  if (isComplete && submitResult) {
    return (
      <section className="section-padding bg-white" data-testid="form-complete-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl p-8 md:p-12 text-center"
          >
            <div className="w-20 h-20 bg-[#10B981] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
              Thank You, {submitResult.full_name.split(' ')[0]}!
            </h2>
            
            <p className="text-lg text-slate-600 mb-8">
              Your free debt assessment request has been received. One of our advisors will contact you shortly.
            </p>

            <div className="bg-white rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-[#0F172A] mb-4">Your Summary:</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Total Debt</p>
                  <p className="font-numbers text-xl font-bold text-[#0F172A]">
                    {formatCurrency(submitResult.debt_amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Est. Monthly Payment</p>
                  <p className="font-numbers text-xl font-bold text-[#10B981]">
                    {formatCurrency(submitResult.estimated_monthly_payment)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">IVA Eligible</p>
                  <p className="font-semibold text-[#0F172A]">
                    {submitResult.iva_eligible ? "Yes - Likely Eligible" : "Assessment Needed"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Potential Savings</p>
                  <p className="font-numbers text-xl font-bold text-[#10B981]">
                    {formatCurrency(submitResult.estimated_savings)}*
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-slate-500">
              *Subject to eligibility and individual circumstances. An advisor will explain all options available to you.
            </p>

            <Button
              onClick={onClose}
              variant="outline"
              className="mt-6"
              data-testid="close-form-btn"
            >
              Close
            </Button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding bg-white relative" data-testid="multi-step-form-section">
      <div className="container-main">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:top-8 md:right-8 p-2 rounded-full hover:bg-slate-100 transition-colors"
          data-testid="close-form-x-btn"
        >
          <X className="w-6 h-6 text-slate-400" />
        </button>

        <div className="max-w-3xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-4">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                
                return (
                  <div key={step.id} className="flex flex-col items-center">
                    <div
                      className={`step-indicator ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : 'inactive'}`}
                      data-testid={`step-indicator-${step.id}`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`text-xs mt-2 font-medium ${isActive ? 'text-[#10B981]' : 'text-slate-400'}`}>
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Form Steps */}
          <AnimatePresence mode="wait">
            {/* Step 1: Debt Level */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
                data-testid="form-step-1"
              >
                <div className="text-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">
                    How much debt do you have?
                  </h2>
                  <p className="text-slate-600">
                    Include all unsecured debts like credit cards, loans, and overdrafts
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-8">
                  <div className="text-center mb-6">
                    <span className="font-numbers text-5xl md:text-6xl font-bold text-[#0F172A]" data-testid="form-debt-display">
                      {formatCurrency(formData.debt_amount)}
                    </span>
                  </div>
                  <Slider
                    value={[formData.debt_amount]}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, debt_amount: value[0] }))}
                    min={5000}
                    max={100000}
                    step={500}
                    className="w-full"
                    data-testid="form-debt-slider"
                  />
                  <div className="flex justify-between mt-3 text-sm text-slate-400">
                    <span>£5,000</span>
                    <span>£100,000+</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Employment & Residential */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
                data-testid="form-step-2"
              >
                <div className="text-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">
                    Tell us about your situation
                  </h2>
                  <p className="text-slate-600">
                    This helps us understand your eligibility
                  </p>
                </div>

                {/* Employment Status */}
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-4">Employment Status</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {EMPLOYMENT_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      const isSelected = formData.employment_status === option.value;
                      return (
                        <button
                          key={option.value}
                          onClick={() => setFormData(prev => ({ ...prev, employment_status: option.value }))}
                          className={`selection-card flex flex-col items-center gap-2 p-4 ${isSelected ? 'selected' : ''}`}
                          data-testid={`employment-${option.value}`}
                        >
                          <Icon className={`w-6 h-6 ${isSelected ? 'text-[#10B981]' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${isSelected ? 'text-[#0F172A]' : 'text-slate-600'}`}>
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Residential Status */}
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-4">Residential Status</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {RESIDENTIAL_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      const isSelected = formData.residential_status === option.value;
                      return (
                        <button
                          key={option.value}
                          onClick={() => setFormData(prev => ({ ...prev, residential_status: option.value }))}
                          className={`selection-card flex flex-col items-center gap-2 p-4 ${isSelected ? 'selected' : ''}`}
                          data-testid={`residential-${option.value}`}
                        >
                          <Icon className={`w-6 h-6 ${isSelected ? 'text-[#10B981]' : 'text-slate-400'}`} />
                          <span className={`text-sm font-medium ${isSelected ? 'text-[#0F172A]' : 'text-slate-600'}`}>
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Monthly Income */}
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-4">Monthly Income (Take-Home)</h3>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="text-center mb-4">
                      <span className="font-numbers text-3xl font-bold text-[#0F172A]" data-testid="form-income-display">
                        {formatCurrency(formData.monthly_income)}
                      </span>
                    </div>
                    <Slider
                      value={[formData.monthly_income]}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, monthly_income: value[0] }))}
                      min={0}
                      max={6000}
                      step={100}
                      className="w-full"
                      data-testid="form-income-slider"
                    />
                    <div className="flex justify-between mt-2 text-xs text-slate-400">
                      <span>£0</span>
                      <span>£6,000+</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Creditors */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
                data-testid="form-step-3"
              >
                <div className="text-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">
                    About your debts
                  </h2>
                  <p className="text-slate-600">
                    Tell us about your creditors and debt types
                  </p>
                </div>

                {/* Number of Creditors */}
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-4">How many creditors do you owe money to?</h3>
                  <div className="flex items-center justify-center gap-6 bg-slate-50 rounded-xl p-6">
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, number_of_creditors: Math.max(1, prev.number_of_creditors - 1) }))}
                      className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 text-slate-600 font-bold text-2xl hover:border-[#10B981] hover:text-[#10B981] transition-colors"
                      data-testid="form-creditor-minus"
                    >
                      -
                    </button>
                    <span className="font-numbers text-5xl font-bold text-[#0F172A] w-20 text-center" data-testid="form-creditor-count">
                      {formData.number_of_creditors}
                    </span>
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, number_of_creditors: Math.min(20, prev.number_of_creditors + 1) }))}
                      className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 text-slate-600 font-bold text-2xl hover:border-[#10B981] hover:text-[#10B981] transition-colors"
                      data-testid="form-creditor-plus"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-center text-sm text-slate-500 mt-2">
                    Minimum 2 creditors required for IVA eligibility
                  </p>
                </div>

                {/* Debt Types */}
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-4">What types of debt do you have? (Select all that apply)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {DEBT_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = formData.debt_types.includes(type.value);
                      return (
                        <button
                          key={type.value}
                          onClick={() => toggleDebtType(type.value)}
                          className={`selection-card flex flex-col items-center gap-2 p-3 ${isSelected ? 'selected' : ''}`}
                          data-testid={`debt-type-${type.value}`}
                        >
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-[#10B981]' : 'text-slate-400'}`} />
                          <span className={`text-xs font-medium text-center ${isSelected ? 'text-[#0F172A]' : 'text-slate-600'}`}>
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Contact Details */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
                data-testid="form-step-4"
              >
                <div className="text-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">
                    Your contact details
                  </h2>
                  <p className="text-slate-600">
                    We'll use this to send you your free assessment
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2">Full Name *</label>
                    <Input
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                      className="form-input"
                      data-testid="form-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2">Email Address *</label>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="form-input"
                      data-testid="form-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="form-input"
                      data-testid="form-phone-input"
                    />
                  </div>

                  {/* Consent checkboxes */}
                  <div className="space-y-3 pt-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consent_terms"
                        checked={formData.consent_terms}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_terms: checked }))}
                        data-testid="form-consent-terms"
                      />
                      <label htmlFor="consent_terms" className="text-sm text-slate-600 cursor-pointer">
                        I agree to the Terms & Conditions and Privacy Policy. I understand my information will be processed to provide debt advice services. *
                      </label>
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consent_marketing"
                        checked={formData.consent_marketing}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_marketing: checked }))}
                        data-testid="form-consent-marketing"
                      />
                      <label htmlFor="consent_marketing" className="text-sm text-slate-600 cursor-pointer">
                        I would like to receive updates and helpful information about debt solutions via email and phone.
                      </label>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-10 pt-6 border-t border-slate-100">
            {currentStep > 1 ? (
              <Button
                variant="outline"
                onClick={handleBack}
                className="px-6"
                data-testid="form-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div></div>
            )}

            {currentStep < 4 ? (
              <Button
                onClick={handleNext}
                className="bg-[#10B981] hover:bg-[#059669] text-white px-8 py-3"
                data-testid="form-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#10B981] hover:bg-[#059669] text-white px-8 py-3"
                data-testid="form-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Submitting...
                  </span>
                ) : (
                  <>
                    Get My Free Assessment
                    <CheckCircle2 className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MultiStepForm;
